import React, { useState, useRef, useEffect } from 'react';
import { sparksAPI } from '../lib/api';
import { useTheme } from '../context/ThemeContext';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { X, ImagePlus, Video, Sparkles, Loader2, AlertTriangle } from 'lucide-react';

const MAX_VIDEO_SECONDS = 34;
const MAX_BYTES = 6 * 1024 * 1024;
const ACCEPTED = 'image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime';

export default function CreateSparkDialog({ onClose, onCreated }) {
  const { isDarkish: isDark } = useTheme();
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [mediaType, setMediaType] = useState(null);
  const [duration, setDuration] = useState(0);
  const [caption, setCaption] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  const reset = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl('');
    setMediaType(null);
    setDuration(0);
    setError('');
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError('');

    if (f.size > MAX_BYTES) {
      setError(`File is too large. Maximum is ${MAX_BYTES / (1024 * 1024)} MB.`);
      return;
    }

    const isImg = f.type.startsWith('image/');
    const isVid = f.type.startsWith('video/');
    if (!isImg && !isVid) {
      setError('Only images or videos are supported.');
      return;
    }

    const url = URL.createObjectURL(f);
    setFile(f);
    setPreviewUrl(url);
    setMediaType(isImg ? 'image' : 'video');

    if (isVid) {
      // Probe duration
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.src = url;
      v.onloadedmetadata = () => {
        const d = v.duration;
        setDuration(d);
        if (d > MAX_VIDEO_SECONDS + 0.5) {
          setError(`Video must be ${MAX_VIDEO_SECONDS} seconds or shorter (your clip is ${d.toFixed(1)}s).`);
        }
      };
    }
  };

  const fileToDataUrl = (f) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(f);
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !mediaType || error) return;
    setSubmitting(true);
    try {
      const dataUrl = await fileToDataUrl(file);
      await sparksAPI.create({
        media_type: mediaType,
        media_data: dataUrl,
        caption: caption.trim() || null,
        duration_seconds: mediaType === 'video' ? duration : null,
      });
      onCreated?.();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to upload Spark');
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = file && mediaType && !error && !submitting;

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="create-spark-dialog"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className={cn(
        'w-full max-w-lg rounded-2xl border shadow-2xl overflow-hidden',
        isDark ? 'bg-[#12121a] border-gray-800' : 'bg-white border-gray-200'
      )}>
        {/* Header */}
        <div className={cn('flex items-center justify-between px-5 py-3 border-b', isDark ? 'border-gray-800' : 'border-gray-200')}>
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-violet-400" />
            <h3 className={cn('text-base font-bold', isDark ? 'text-white' : 'text-gray-900')}>New Spark</h3>
            <span className={cn('text-[10px] px-1.5 py-0.5 rounded-full font-medium', isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500')}>
              expires in 24h
            </span>
          </div>
          <button
            onClick={onClose}
            className={cn('w-8 h-8 rounded-full flex items-center justify-center transition', isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500')}
            aria-label="Close"
            data-testid="create-spark-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {!file ? (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className={cn(
                'w-full aspect-[9/16] max-h-[55vh] rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-3 transition-colors',
                isDark ? 'border-gray-700 hover:border-violet-500/60 hover:bg-violet-500/5 text-gray-400' : 'border-gray-300 hover:border-violet-500 hover:bg-violet-50 text-gray-500'
              )}
              data-testid="spark-pick-file"
            >
              <div className="flex gap-3">
                <ImagePlus className="h-7 w-7 text-violet-400" />
                <Video className="h-7 w-7 text-pink-400" />
              </div>
              <p className="text-sm font-medium">Tap to pick image or video</p>
              <p className="text-[11px] opacity-70 max-w-xs text-center">
                Up to 6 MB · video clips capped at <strong>{MAX_VIDEO_SECONDS}s</strong>
              </p>
            </button>
          ) : (
            <div className="relative rounded-xl overflow-hidden bg-black aspect-[9/16] max-h-[55vh]">
              {mediaType === 'image' ? (
                <img src={previewUrl} alt="Preview" className="w-full h-full object-contain" />
              ) : (
                <video src={previewUrl} controls playsInline className="w-full h-full object-contain" data-testid="spark-video-preview" />
              )}
              <button
                type="button"
                onClick={reset}
                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
                aria-label="Remove media"
                data-testid="spark-remove-file"
              >
                <X className="h-4 w-4" />
              </button>
              {mediaType === 'video' && duration > 0 && (
                <span className={cn(
                  'absolute bottom-2 left-2 px-2 py-1 rounded-md text-[10px] font-mono font-bold',
                  duration > MAX_VIDEO_SECONDS ? 'bg-red-500 text-white' : 'bg-black/60 text-white'
                )}>
                  {duration.toFixed(1)}s / {MAX_VIDEO_SECONDS}s
                </span>
              )}
            </div>
          )}

          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED}
            onChange={onFileChange}
            className="hidden"
            data-testid="spark-file-input"
          />

          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value.slice(0, 200))}
            placeholder="Add a caption (optional)…"
            rows={2}
            className={cn(
              'w-full px-3 py-2 rounded-lg text-sm border resize-none outline-none focus:ring-2 focus:ring-violet-500/30',
              isDark ? 'bg-[#0a0a0f] border-gray-800 text-white placeholder:text-gray-500' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400'
            )}
            data-testid="spark-caption"
          />

          {error && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs" data-testid="spark-error">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex items-center gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
              className={cn('flex-1', isDark ? 'border-gray-700 text-gray-300 bg-transparent hover:bg-gray-800' : '')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit}
              className="flex-1 bg-gradient-to-r from-violet-600 via-fuchsia-600 to-pink-600 hover:from-violet-700 hover:via-fuchsia-700 hover:to-pink-700 text-white"
              data-testid="spark-submit"
            >
              {submitting ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Posting…</> : 'Share Spark'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
